import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const SetupSystem = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeTab, setActiveTab] = useState('hotel');

  // Data states
  const [settings, setSettings] = useState({});
  const [policies, setPolicies] = useState([]);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  // Color palette
  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444'
  };

  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const settingsData = await apiGet('/admin/setup/system/settings');
      if (settingsData.success) setSettings(settingsData.data);

      if (activeTab === 'cancellation') {
        const policiesData = await apiGet('/admin/setup/system/cancellation-policies');
        if (policiesData.success) setPolicies(policiesData.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handleUpdateSetting = async (key, value) => {
    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/setup/system/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value })
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ Setting updated successfully!');
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating setting:', error);
      alert('Error updating setting');
    }
  };

  const handleSavePolicy = async (formData) => {
    try {
      const url = editingPolicy
        ? `${PRODUCTION_URL}/api/admin/setup/system/cancellation-policies/${editingPolicy.id}`
        : `${PRODUCTION_URL}/api/admin/setup/system/cancellation-policies`;

      const method = editingPolicy ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        alert(editingPolicy ? '✅ Policy updated!' : '✅ Policy created!');
        setShowPolicyModal(false);
        setEditingPolicy(null);
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving policy:', error);
      alert('Error saving policy');
    }
  };

  const handleDeletePolicy = async (id) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/setup/system/cancellation-policies/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        alert('✅ Policy deleted!');
        fetchData();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting policy:', error);
      alert('Error deleting policy');
    }
  };

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '16px' : '24px',
      padding: isMobile ? '0 4px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '20px' : '28px',
      fontWeight: '700',
      color: colors.textDark,
      marginBottom: '6px'
    },
    pageSubtitle: {
      fontSize: isMobile ? '12px' : '14px',
      color: colors.textLight
    },
    tabContainer: {
      display: 'flex',
      gap: isMobile ? '8px' : '12px',
      marginBottom: isMobile ? '16px' : '20px',
      padding: isMobile ? '0 4px' : '0',
      flexWrap: 'wrap'
    },
    tab: (active) => ({
      padding: isMobile ? '10px 16px' : '12px 24px',
      background: active ? colors.primary : colors.white,
      color: active ? colors.white : colors.textDark,
      border: `2px solid ${active ? colors.primary : colors.mediumGray}`,
      borderRadius: '10px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }),
    contentCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.08)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 4px' : '0'
    },
    formGroup: {
      marginBottom: isMobile ? '16px' : '20px'
    },
    label: {
      display: 'block',
      marginBottom: '6px',
      fontSize: isMobile ? '12px' : '13px',
      fontWeight: '600',
      color: colors.textDark
    },
    input: {
      width: '100%',
      padding: isMobile ? '10px' : '12px',
      borderRadius: '8px',
      border: `1px solid ${colors.mediumGray}`,
      fontSize: isMobile ? '13px' : '14px',
      boxSizing: 'border-box'
    },
    button: {
      padding: isMobile ? '10px 16px' : '12px 20px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: '8px',
      fontSize: isMobile ? '13px' : '14px',
      fontWeight: '600',
      cursor: 'pointer'
    },
    sectionTitle: {
      fontSize: isMobile ? '16px' : '18px',
      fontWeight: '700',
      marginBottom: isMobile ? '16px' : '20px',
      color: colors.textDark,
      paddingBottom: '10px',
      borderBottom: `2px solid ${colors.lightGray}`
    }
  };

  const SettingInput = ({ setting, value, onChange }) => {
    if (setting.setting_type === 'boolean') {
      return (
        <select
          value={value}
          onChange={(e) => onChange(setting.setting_key, e.target.value)}
          onBlur={() => handleUpdateSetting(setting.setting_key, value)}
          style={styles.input}
        >
          <option value="1">Yes</option>
          <option value="0">No</option>
        </select>
      );
    }

    if (setting.setting_type === 'textarea') {
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(setting.setting_key, e.target.value)}
          onBlur={() => handleUpdateSetting(setting.setting_key, value)}
          rows={3}
          style={{ ...styles.input, resize: 'vertical' }}
        />
      );
    }

    return (
      <input
        type={setting.setting_type === 'number' ? 'number' :
          setting.setting_type === 'email' ? 'email' :
            setting.setting_type === 'time' ? 'time' : 'text'}
        value={value}
        onChange={(e) => onChange(setting.setting_key, e.target.value)}
        onBlur={() => handleUpdateSetting(setting.setting_key, value)}
        style={styles.input}
      />
    );
  };

  const PolicyModal = () => {
    const [formData, setFormData] = useState(editingPolicy || {
      policy_name: '',
      hours_before_checkin: 24,
      cancellation_charge_percentage: 0,
      is_active: 1,
      is_default: 0
    });

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
        <div style={{
          background: colors.white,
          padding: isMobile ? '20px' : '30px',
          borderRadius: '16px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}>
          <h3 style={{ marginBottom: '20px', fontSize: isMobile ? '18px' : '22px' }}>
            {editingPolicy ? 'Edit Policy' : 'Create Cancellation Policy'}
          </h3>

          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <label style={styles.label}>Policy Name</label>
              <input
                type="text"
                value={formData.policy_name}
                onChange={(e) => setFormData({ ...formData, policy_name: e.target.value })}
                placeholder="e.g., Flexible Cancellation"
                style={styles.input}
              />
            </div>

            <div>
              <label style={styles.label}>Hours Before Check-in</label>
              <input
                type="number"
                value={formData.hours_before_checkin}
                onChange={(e) => setFormData({ ...formData, hours_before_checkin: e.target.value })}
                placeholder="24"
                style={styles.input}
              />
              <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '4px' }}>
                Cancel without charge if done this many hours before check-in
              </div>
            </div>

            <div>
              <label style={styles.label}>Cancellation Charge (%)</label>
              <input
                type="number"
                value={formData.cancellation_charge_percentage}
                onChange={(e) => setFormData({ ...formData, cancellation_charge_percentage: e.target.value })}
                placeholder="0"
                min="0"
                max="100"
                style={styles.input}
              />
              <div style={{ fontSize: '11px', color: colors.textLight, marginTop: '4px' }}>
                Percentage of booking amount to charge
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                checked={formData.is_default === 1}
                onChange={(e) => setFormData({ ...formData, is_default: e.target.checked ? 1 : 0 })}
                id="is_default"
              />
              <label htmlFor="is_default" style={{ fontSize: '14px', fontWeight: '600' }}>
                Set as default policy
              </label>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowPolicyModal(false);
                setEditingPolicy(null);
              }}
              style={{
                padding: '10px 20px',
                background: colors.mediumGray,
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button onClick={() => handleSavePolicy(formData)} style={styles.button}>
              {editingPolicy ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div>Loading settings...</div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>System Setup</h1>
        <p style={styles.pageSubtitle}>
          {isMobile ? 'Hotel configuration' : 'Configure hotel settings, system preferences, and operational parameters'}
        </p>
      </div>

      {/* Tabs */}
      <div style={styles.tabContainer}>
        <button style={styles.tab(activeTab === 'hotel')} onClick={() => setActiveTab('hotel')}>
          🏨 Hotel Info
        </button>
        <button style={styles.tab(activeTab === 'operational')} onClick={() => setActiveTab('operational')}>
          ⚙️ Operations
        </button>
        <button style={styles.tab(activeTab === 'tax')} onClick={() => setActiveTab('tax')}>
          💰 Tax & Payment
        </button>
        <button style={styles.tab(activeTab === 'cancellation')} onClick={() => setActiveTab('cancellation')}>
          📋 Cancellation
        </button>
      </div>

      {/* Content */}
      <div style={styles.contentCard}>
        {/* Hotel Information Tab */}
        {activeTab === 'hotel' && settings.hotel && (
          <div>
            <h3 style={styles.sectionTitle}>Hotel Information</h3>
            <div style={{ display: 'grid', gap: isMobile ? '16px' : '20px' }}>
              {settings.hotel.map(setting => (
                <div key={setting.id} style={styles.formGroup}>
                  <label style={styles.label}>{setting.description}</label>
                  <SettingInput
                    setting={setting}
                    value={setting.setting_value || ''}
                    onChange={(key, value) => {
                      const newSettings = { ...settings };
                      const settingIndex = newSettings.hotel.findIndex(s => s.setting_key === key);
                      if (settingIndex !== -1) {
                        newSettings.hotel[settingIndex].setting_value = value;
                        setSettings(newSettings);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Operational Settings Tab */}
        {activeTab === 'operational' && settings.operational && (
          <div>
            <h3 style={styles.sectionTitle}>Operational Settings</h3>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '16px' : '20px' }}>
              {settings.operational.map(setting => (
                <div key={setting.id} style={styles.formGroup}>
                  <label style={styles.label}>{setting.description}</label>
                  <SettingInput
                    setting={setting}
                    value={setting.setting_value || ''}
                    onChange={(key, value) => {
                      const newSettings = { ...settings };
                      const settingIndex = newSettings.operational.findIndex(s => s.setting_key === key);
                      if (settingIndex !== -1) {
                        newSettings.operational[settingIndex].setting_value = value;
                        setSettings(newSettings);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tax & Payment Tab */}
        {activeTab === 'tax' && (
          <div>
            <h3 style={styles.sectionTitle}>Tax Settings</h3>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '16px' : '20px', marginBottom: '30px' }}>
              {settings.tax?.map(setting => (
                <div key={setting.id} style={styles.formGroup}>
                  <label style={styles.label}>{setting.description}</label>
                  <SettingInput
                    setting={setting}
                    value={setting.setting_value || ''}
                    onChange={(key, value) => {
                      const newSettings = { ...settings };
                      const settingIndex = newSettings.tax.findIndex(s => s.setting_key === key);
                      if (settingIndex !== -1) {
                        newSettings.tax[settingIndex].setting_value = value;
                        setSettings(newSettings);
                      }
                    }}
                  />
                </div>
              ))}
            </div>

            <h3 style={styles.sectionTitle}>Payment Gateway</h3>
            <div style={{ display: 'grid', gap: isMobile ? '16px' : '20px' }}>
              {settings.payment?.map(setting => (
                <div key={setting.id} style={styles.formGroup}>
                  <label style={styles.label}>{setting.description}</label>
                  <SettingInput
                    setting={setting}
                    value={setting.setting_value || ''}
                    onChange={(key, value) => {
                      const newSettings = { ...settings };
                      const settingIndex = newSettings.payment.findIndex(s => s.setting_key === key);
                      if (settingIndex !== -1) {
                        newSettings.payment[settingIndex].setting_value = value;
                        setSettings(newSettings);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cancellation Policies Tab */}
        {activeTab === 'cancellation' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: '700' }}>Cancellation Policies</h3>
              <button
                onClick={() => {
                  setEditingPolicy(null);
                  setShowPolicyModal(true);
                }}
                style={styles.button}
              >
                + New Policy
              </button>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              {policies.map(policy => (
                <div
                  key={policy.id}
                  style={{
                    padding: isMobile ? '16px' : '20px',
                    background: policy.is_default ? colors.lightGray : colors.white,
                    border: `2px solid ${policy.is_default ? colors.primary : colors.mediumGray}`,
                    borderRadius: '12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                        <strong style={{ fontSize: isMobile ? '15px' : '16px' }}>{policy.policy_name}</strong>
                        {policy.is_default && (
                          <span style={{
                            padding: '2px 8px',
                            background: colors.primary,
                            color: colors.white,
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: '600'
                          }}>
                            DEFAULT
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: isMobile ? '12px' : '13px', color: colors.textLight }}>
                        Cancel {policy.hours_before_checkin} hours before check-in
                      </div>
                      <div style={{ fontSize: isMobile ? '13px' : '14px', color: colors.textDark, marginTop: '4px' }}>
                        Charge: <strong>{policy.cancellation_charge_percentage}%</strong> of booking amount
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => {
                          setEditingPolicy(policy);
                          setShowPolicyModal(true);
                        }}
                        style={{
                          padding: '6px 14px',
                          background: colors.accent,
                          color: colors.white,
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          cursor: 'pointer'
                        }}
                      >
                        Edit
                      </button>
                      {!policy.is_default && (
                        <button
                          onClick={() => handleDeletePolicy(policy.id)}
                          style={{
                            padding: '6px 14px',
                            background: colors.danger,
                            color: colors.white,
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showPolicyModal && <PolicyModal />}
    </AdminLayout>
  );
};

export default SetupSystem;